// "Your family": the people list. Each name links to that person's own page, where a
// principal changes them and sees what Mor has been told about them; this page only lists
// them and adds a new one.
//
// The add goes to the server as one direct change to the record and the screen follows on
// the refetch. Any refusal is shown as the server's own sentence.

import { useState } from 'react';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type { RecordChange } from '../api/contract.js';
import { changedLine } from '../format.js';
import type { Internals } from '../internals.js';
import { changedClass, useChangedIds } from '../plan/useChangedIds.js';
import { viewerIsPrincipal } from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';
import { initialsOf, hueOf } from './PersonRow.js';
import { FormButtons, looksLikeEmail, NOT_AN_EMAIL, cancelOnEscape } from './RowEdit.js';

export type FamilyPageProps = {
  state: PlanState;
  internals: Internals;
  client: ApiClient;
  planId: string;
};

export function FamilyPage({
  state,
  internals,
  client,
  planId,
}: FamilyPageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const changed = useChangedIds(snapshot?.people ?? null, (p) =>
    JSON.stringify([p.name, p.email, p.joined, p.invited]),
  );
  if (snapshot === null) {
    return null;
  }
  const { PersonRow } = internals;
  const canEdit = viewerIsPrincipal(snapshot);

  /** Sends one change. Resolves true when it was accepted. */
  const send = async (change: RecordChange): Promise<boolean> => {
    setBusy(true);
    setProblem(null);
    try {
      await client.changeRecord(planId, change);
      state.refetch();
      return true;
    } catch (error) {
      setProblem(
        error instanceof ApiFailure && error.code === 'refused'
          ? error.message
          : 'That didn’t save. Try again.',
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  const closeForms = (): void => {
    setProblem(null);
    setAdding(false);
  };

  return (
    <section className="page">
      <h2 className="page__title">Tell us about your family</h2>
      <p className="page__subtitle">
        The people whose interests, roles or relationships matter to what you're here to
        work through.
      </p>
      <ul className="people__list">
        {[...snapshot.people]
          .sort((a, b) => Number(b.self) - Number(a.self))
          .map((person) => (
            <PersonRow
              key={person.id}
              person={person}
              to={`/plans/${planId}/people/${person.id}`}
              highlightClass={changedClass(changed, person.id)}
              changedNote={changedLine(
                person.changedBy,
                person.changedAt,
                person.createdAt,
                snapshot.people,
              )}
            />
          ))}
      </ul>
      {canEdit ? (
        adding ? (
          <AddPersonForm
            busy={busy}
            error={problem}
            onCancel={closeForms}
            onSave={async (name, email) => {
              const saved = await send(
                email === ''
                  ? { kind: 'add_person', name }
                  : { kind: 'add_person', name, email },
              );
              if (saved) {
                setAdding(false);
              }
            }}
          />
        ) : (
          <button
            type="button"
            className="addPerson"
            onClick={() => {
              setProblem(null);
              setAdding(true);
            }}
          >
            <span className="addPerson__plus" aria-hidden="true">
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M7 2v10" />
                <path d="M2 7h10" />
              </svg>
            </span>
            Add person
          </button>
        )
      ) : null}
      {problem === null || adding ? null : (
        <p className="page__problem" role="alert">
          {problem}
        </p>
      )}
    </section>
  );
}

function AddPersonForm({
  busy,
  error,
  onCancel,
  onSave,
}: {
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (name: string, email: string) => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [shape, setShape] = useState<string | null>(null);
  const typed = name.trim() !== '';
  return (
    <div className="personAdd">
      <div className="person person--adding">
        <span
          className={
            typed
              ? `person__avatar person__avatar--${hueOf(name)}`
              : 'person__avatar person__avatar--empty'
          }
          aria-hidden="true"
        >
          {typed ? initialsOf(name) : ''}
        </span>
        <form
          className="inlineForm inlineForm--add"
          noValidate
          onKeyDown={cancelOnEscape(onCancel)}
          onSubmit={(event) => {
            event.preventDefault();
            if (email.trim() !== '' && !looksLikeEmail(email.trim())) {
              setShape(NOT_AN_EMAIL);
              return;
            }
            if (typed && !busy) {
              void onSave(name.trim(), email.trim());
            }
          }}
        >
          <input
            className="inlineForm__input"
            aria-label="Name"
            placeholder="Name"
            value={name}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
          <input
            className="inlineForm__input"
            type="email"
            aria-label="Email"
            placeholder="Email (optional)"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setShape(null);
            }}
          />
          <FormButtons submitLabel="Add" disabled={busy || !typed} onCancel={onCancel} />
          {(shape ?? error) === null ? null : (
            <p className="inlineForm__error">{shape ?? error}</p>
          )}
        </form>
      </div>
    </div>
  );
}
